import {
    ResponsiveContainer,
    LineChart,
    CartesianGrid,
    XAxis,
    YAxis,
    Tooltip,
    Line,
    Legend
  } from 'recharts';

import { format, addDays, differenceInDays } from 'date-fns';
import { useMemo } from 'react'; 

  
export default function ProductTrendChart({ selectedChartStartDate, selectedChartEndDate, selectedTrendProduct, chartProducts }) {
  
  const getDateRangeArray = (start, end) => {
    const dates = [];
    const totalDays = differenceInDays(end, start);
    for (let i = 0; i <= totalDays; i++) {
      const date = addDays(start, i);
      dates.push(format(date, 'yyyy-MM-dd'));
    }
    return dates;
  };

  const mergedTrendData = useMemo(() => {

    if (!selectedChartStartDate || !selectedChartEndDate) return [];

    const dateRange = getDateRangeArray(selectedChartStartDate, selectedChartEndDate);
    const dateMap = {};

    // 날짜만 먼저 준비 (X축을 위해 필요)
    dateRange.forEach(date => {
      dateMap[date] = { date, __dummy: 0 };
      // 모든 선택된 상품에 대해 기본값 11 세팅
      selectedTrendProduct.forEach(p => {
        dateMap[date][p.ProductName] = 11;
      });
    });

    // 선택된 상품 이름 목록
    const selectedNames = selectedTrendProduct.map(p => p.ProductName);

    // trend 데이터를 날짜별로 상품별로 넣기
    chartProducts.forEach(({ G_Date, ProductName, G_Rank }) => {
      if (selectedNames.includes(ProductName) && dateMap[G_Date["value"]]) {
        dateMap[G_Date["value"]][ProductName] = G_Rank !== undefined && G_Rank < 11 ? G_Rank : 11; // 순위밖
      }
    });

    return Object.values(dateMap); // 항상 날짜 포함된 데이터 리턴
  }, [selectedChartStartDate, selectedChartEndDate, chartProducts, selectedTrendProduct]);

  return (
      <ResponsiveContainer width="100%" height={370}>
        <LineChart 
          data={mergedTrendData}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis 
            dataKey="date" 
            tick={{ fontSize: 9 }}
            tickMargin={16}
            padding={{ left: 20, right: 0 }}
          />
          <YAxis
            domain={[1, 11]}
            reversed
            allowDecimals={false}
            interval={0}
            tick={{ fontSize: 9 }}
            ticks={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11]} // 원하는 숫자만 나열
            tickFormatter={(value) => value === 11 ? "순위밖" : value}
          />
          <Tooltip 
            contentStyle={{ fontSize: "9px" }}
            itemStyle={{ fontSize: "9px" }}
            formatter={(value, name) => {
              // name은 legend에 들어간 값이라 생략된 상태
              const matched = selectedTrendProduct.find(p => {
                const shortened = p.ProductName.length > 18 ? p.ProductName.slice(0, 18) + "..." : p.ProductName;
                return shortened === name;
              });

              // value가 -5000이면 '순위밖'으로 바꿔줌
              const displayValue = value === 11 ? '순위밖' : value;

              return [displayValue, matched?.ProductName || name]; // 실제 전체 상품명 표시
            }}
          />
          <Legend 
            verticalAlign="top" 
            align="right" 
            layout="vertical" 
            wrapperStyle={{
              fontSize: 9, // 글자 크기 조정
              width: 160,
              paddingLeft: 28,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis"
            }}
          />
          {/* 선택된 상품 없을 때 더미 Line 추가 */}
          {selectedTrendProduct.length === 0 ? (
            <Line
              dataKey="__dummy"
              stroke="transparent"
              dot={false}
              activeDot={false}
              isAnimationActive={false}
              margin={{ top: 10, right: 30, left: 30, bottom: 30 }}
            />
          ) : (
            selectedTrendProduct.map((product, index) => (
              <Line
                key={product.ProductName}
                type="monotone"
                dataKey={product.ProductName}
                name={product.ProductName.length > 18 ? product.ProductName.slice(0, 18) + "..." : product.ProductName}
                stroke={`hsl(${(index * 60) % 360}, 70%, 50%)`}
                strokeWidth={2}
                margin={{ top: 10, right: 30, left: 30, bottom: 30 }}
              />
            ))
          )}
        </LineChart>
      </ResponsiveContainer>
  );
}